import { useMemo, useCallback, useSyncExternalStore } from "react";

export type AvailabilityFilter = "all" | "in-stock" | "arriving-soon";

const VALID_AVAILABILITY_VALUES: AvailabilityFilter[] = ["all", "in-stock", "arriving-soon"];

interface FilterState {
  brands: string[];
  categoryBrands: Record<string, string[]>;
  categories: string[];
  grades: string[];
  /**
   * Per-subcategory grade exclusion list (mirror of `categoryBrands` shape).
   * When a subcategory has its own entry, it overrides the global `grades`
   * filter for products in that subcategory. The global `grades` param is
   * kept as a backwards-compatible fallback for shared bookmarks/links.
   */
  categoryGrades: Record<string, string[]>;
  warehouseLocations: string[];
  sortBy: string;
  priceRange: number[];
  inStockOnly: boolean;
  availability: AvailabilityFilter;
  searchTerm: string;
}

/**
 * Parse a `key:val|val;key:val` URL param into a Record<string, string[]>.
 * Empty value lists ARE preserved (entry `key:` parses to `{ key: [] }`) so
 * we can distinguish "explicitly emptied by the user" from "never selected
 * at all". Without this distinction the catalog falls back to its
 * "newly added category — show everything" branch as soon as the user
 * deselects the last brand pill in a subcategory, making it look like the
 * filter pills aren't working.
 */
function parseRecordList(param: string | null): Record<string, string[]> {
  if (!param) return {};
  const result: Record<string, string[]> = {};
  param.split(';').filter(Boolean).forEach(segment => {
    const colonIdx = segment.indexOf(':');
    const catId = colonIdx === -1 ? segment : segment.slice(0, colonIdx);
    const valuesStr = colonIdx === -1 ? '' : segment.slice(colonIdx + 1);
    if (catId) {
      result[catId] = valuesStr ? valuesStr.split('|').filter(Boolean) : [];
    }
  });
  return result;
}

function serializeRecordList(record: Record<string, string[]>): string {
  return Object.entries(record)
    .map(([catId, values]) => `${catId}:${values.join('|')}`)
    .join(';');
}

const parseCategoryBrands = parseRecordList;
const serializeCategoryBrands = serializeRecordList;

interface PaginationState {
  currentPage: number;
  itemsPerPage: number;
}

const VALID_ITEMS_PER_PAGE = [25, 50, 100];
const DEFAULT_ITEMS_PER_PAGE = 25;

// Module-level event emitter for URL changes
let listeners: Array<() => void> = [];

function notifyListeners() {
  listeners.forEach(listener => listener());
}

// Clean subscription: only popstate, no monkey-patching
function subscribe(callback: () => void) {
  listeners.push(callback);
  window.addEventListener('popstate', callback);
  
  return () => {
    listeners = listeners.filter(l => l !== callback);
    window.removeEventListener('popstate', callback);
  };
}

function getSnapshot() {
  return window.location.search;
}

export function useUrlFilters() {
  // Subscribe to URL changes via useSyncExternalStore for reliable state sync
  const search = useSyncExternalStore(subscribe, getSnapshot);

  // Parse current URL into filters and pagination (SINGLE SOURCE OF TRUTH)
  const { filters, pagination } = useMemo(() => {
    const searchParams = new URLSearchParams(search);
    
    // Parse pagination
    const urlPage = parseInt(searchParams.get('page') || '1');
    const urlItemsPerPage = parseInt(searchParams.get('per_page') || String(DEFAULT_ITEMS_PER_PAGE));
    const validatedItemsPerPage = VALID_ITEMS_PER_PAGE.includes(urlItemsPerPage) 
      ? urlItemsPerPage 
      : DEFAULT_ITEMS_PER_PAGE;
    const sanitizedPage = isNaN(urlPage) || urlPage < 1 ? 1 : urlPage;
    
    // Parse filters
    const urlLocations = searchParams.get('warehouses')?.split(',').filter(Boolean) || [];
    const urlGrades = searchParams.get('grades')?.split(',').filter(Boolean) || [];
    const urlBrands = searchParams.get('brands')?.split(',').filter(Boolean) || [];
    const urlCategoryBrands = parseCategoryBrands(searchParams.get('catBrands'));
    const urlCategoryGrades = parseRecordList(searchParams.get('catGrades'));
    const urlCategories = searchParams.get('categories')?.split(',').filter(Boolean) || [];
    
    // No default brands - defaults are now set in product-gallery after inventory loads
    // This allows us to select ALL Appliances subcategories and brands as defaults
    const urlSortBy = searchParams.get('sort') || 'price_low';
    const urlInStockOnly = searchParams.get('inStock') === 'true';
    const urlSearchTerm = searchParams.get('search') || '';
    const urlPriceMin = parseInt(searchParams.get('priceMin') || '0');
    const urlPriceMax = parseInt(searchParams.get('priceMax') || '5000');
    const rawAvailability = searchParams.get('availability') as AvailabilityFilter | null;
    const urlAvailability: AvailabilityFilter = rawAvailability && VALID_AVAILABILITY_VALUES.includes(rawAvailability)
      ? rawAvailability
      : 'all';
    
    return {
      filters: {
        brands: urlBrands,
        categoryBrands: urlCategoryBrands,
        categories: urlCategories,
        grades: urlGrades as Array<"A" | "B+" | "B-">,
        categoryGrades: urlCategoryGrades,
        warehouseLocations: urlLocations,
        sortBy: urlSortBy,
        priceRange: [urlPriceMin, urlPriceMax],
        inStockOnly: urlInStockOnly,
        availability: urlAvailability,
        searchTerm: urlSearchTerm,
      } as FilterState,
      pagination: {
        currentPage: sanitizedPage,
        itemsPerPage: validatedItemsPerPage,
      } as PaginationState
    };
  }, [search]);

  // Update URL with new filter/pagination values
  // Read from window.location.search directly to avoid stale closures
  const updateUrl = useCallback((updates: {
    filters?: Partial<FilterState>;
    pagination?: Partial<PaginationState>;
    resetPage?: boolean;
  }) => {
    // Read fresh URL from window.location to avoid stale location from closure
    const searchParams = new URLSearchParams(window.location.search);
    
    // Parse current filters from URL
    const currentFilters = {
      brands: (searchParams.get('brands')?.split(',') || []).filter(Boolean),
      categoryBrands: parseCategoryBrands(searchParams.get('catBrands')),
      categories: (searchParams.get('categories')?.split(',') || []).filter(Boolean),
      grades: (searchParams.get('grades')?.split(',') || []).filter(Boolean) as Array<"A" | "B+" | "B-">,
      categoryGrades: parseRecordList(searchParams.get('catGrades')),
      warehouseLocations: (searchParams.get('warehouses')?.split(',') || []).filter(Boolean),
      sortBy: searchParams.get('sort') || 'price_low',
      priceRange: [
        parseInt(searchParams.get('priceMin') || '0'),
        parseInt(searchParams.get('priceMax') || '5000')
      ],
      inStockOnly: searchParams.get('inStock') === 'true',
      availability: (() => {
        const raw = searchParams.get('availability') as AvailabilityFilter | null;
        return raw && VALID_AVAILABILITY_VALUES.includes(raw) ? raw : 'all';
      })(),
      searchTerm: searchParams.get('search') || '',
    } as FilterState;
    
    // Parse current pagination from URL
    const urlPage = parseInt(searchParams.get('page') || '1');
    const urlItemsPerPage = parseInt(searchParams.get('per_page') || String(DEFAULT_ITEMS_PER_PAGE));
    const currentPagination = {
      currentPage: isNaN(urlPage) || urlPage < 1 ? 1 : urlPage,
      itemsPerPage: VALID_ITEMS_PER_PAGE.includes(urlItemsPerPage) ? urlItemsPerPage : DEFAULT_ITEMS_PER_PAGE
    };
    
    // Merge current state with updates
    const newFilters = { ...currentFilters, ...updates.filters };
    const newPagination = { ...currentPagination, ...updates.pagination };
    
    
    const params = new URLSearchParams();
    
    // Reset page to 1 if filters changed (unless explicitly told not to)
    const page = updates.resetPage !== false && updates.filters 
      ? 1 
      : newPagination.currentPage;
    
    // Set pagination params
    params.set('page', String(page));
    params.set('per_page', String(newPagination.itemsPerPage));
    
    // Set filter params (only if non-empty)
    if (newFilters.warehouseLocations.length > 0) {
      params.set('warehouses', newFilters.warehouseLocations.join(','));
    }
    if (newFilters.grades.length > 0) {
      params.set('grades', newFilters.grades.join(','));
    }
    if (newFilters.brands.length > 0) {
      params.set('brands', newFilters.brands.join(','));
    }
    const catBrandsStr = serializeCategoryBrands(newFilters.categoryBrands || {});
    if (catBrandsStr) {
      params.set('catBrands', catBrandsStr);
    }
    const catGradesStr = serializeRecordList(newFilters.categoryGrades || {});
    if (catGradesStr) {
      params.set('catGrades', catGradesStr);
    }
    if (newFilters.categories.length > 0) {
      params.set('categories', newFilters.categories.join(','));
    }
    if (newFilters.sortBy !== 'price_low') {
      params.set('sort', newFilters.sortBy);
    }
    if (newFilters.inStockOnly) {
      params.set('inStock', 'true');
    }
    if (newFilters.availability && newFilters.availability !== 'all') {
      params.set('availability', newFilters.availability);
    }
    if (newFilters.searchTerm) {
      params.set('search', newFilters.searchTerm);
    }
    if (newFilters.priceRange[0] !== 0) {
      params.set('priceMin', String(newFilters.priceRange[0]));
    }
    if (newFilters.priceRange[1] !== 5000) {
      params.set('priceMax', String(newFilters.priceRange[1]));
    }
    
    const basePath = window.location.pathname;
    const newUrl = `${basePath}?${params.toString()}`;
    
    // Save current scroll position before any updates
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    
    // Use history.replaceState directly to avoid scroll-to-top behavior
    window.history.replaceState(null, '', newUrl);
    
    // Notify listeners to sync React state with the new URL
    notifyListeners();
    
    // Restore scroll position after React re-renders
    // Use multiple RAF calls to ensure we run after all rendering phases
    const restoreScroll = () => {
      if (window.scrollY !== scrollY || window.scrollX !== scrollX) {
        window.scrollTo(scrollX, scrollY);
      }
    };
    
    // Try immediately, after RAF, and after a short delay to catch all render phases
    restoreScroll();
    requestAnimationFrame(() => {
      restoreScroll();
      requestAnimationFrame(restoreScroll);
    });
    setTimeout(restoreScroll, 50);
  }, []);

  return {
    filters,
    pagination,
    updateUrl,
  };
}
